import React, { useState, useEffect } from "react";

// Importing bootstrap components
import {
  Row,
  Col,
  Card,
  Table,
  Tooltip,
  OverlayTrigger,
  Form,
  FormSelect,
  Button,
} from "react-bootstrap";

// Importing redux components
import { useSelector, useDispatch } from "react-redux";

import { useGetAttendancesByIdQuery } from "state/slices/attendance/attendanceApiSlice";

// WFH Redux imports
import {
  setWfhs,
  addWfh as addWfhToStore,
  updateWfh as updateWfhInStore,
  setIsPunchedOut,
  setShowPunchWarning
} from "state/slices/wfh/wfhSlice";

import {
  useCreateWfhMutation,
  useGetWfhsQuery,
  useUpdateWfhMutation,
} from "state/slices/wfh/wfhApiSlice";

// Import attendance field counter
import { FieldCounter } from "utils/FieldCounter";

// Import HRPagination Component
import HRPagination from "components/HRPagination";

// Import UpcomingHoliday Component
import UpcomingHoliday from "components/UpcomingHoliday";
import PayrollNote from "components/PayrollNote";
import SelfieCapture from "components/SelfieCapture";
import ScreenRecordingBanner from "components/ScreenRecordingBanner";
import { DateTime } from "luxon";
import { showSuccessToast } from "utils/toast";

const GeneralAccessManagement = ({ isAllowedToReadPayrollNotes }) => {
  const dispatch = useDispatch();
  const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

  // Employee Id
  const employeeId = loggedUserInfo?.employeeId;

  const currentMonthDate = DateTime.now().minus({ months: 1 });
  const currentMonthNumber = currentMonthDate.month;
  const currentMonthName = currentMonthDate.toFormat("MMMM");
  const previousMonthDate = DateTime.now().minus({ months: 2 });
  const previousMonthNumber = previousMonthDate.month;
  const previousMonthName = previousMonthDate.toFormat("MMMM");

  const currentDate = new Date();
  const attendanceYear =
    currentDate.getMonth() !== 0
      ? currentDate.getFullYear()
      : currentDate.getFullYear() - 1;

  const ITEMS_PER_PAGE = 10;

  const [selectedMonth, setSelectedMonth] = useState(currentMonthNumber);
  const [currentData, setCurrentData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [fieldCounts, setFieldCounts] = useState({ late: 0, ot: 0 });

  // WFH State
  const [dateTimeString, setDateTimeString] = useState("");
  const [processing, setProcessing] = useState({
    punchIn: false,
    punchOut: false,
  });
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [selfieFile, setSelfieFile] = useState(null);

  const wfhs = useSelector((state) => state.wfh.wfhs);
  const isPunchedOut = useSelector((state) => state.wfh.isPunchedOut);
  const showPunchWarning = useSelector((state) => state.wfh.showPunchWarning);

  // Fetch attendances and handle errors using the selected month
  const {
    data: attendancesData,
    isLoading: isAttendancesLoading,
    isError: isAttendancesError,
  } = useGetAttendancesByIdQuery({
    year: attendanceYear,
    month: selectedMonth,
    id: employeeId,
  });

  // WFH Queries
  const {
    data: wfhData,
    refetch: refetchWfh,
    isLoading: isWFHsLoading,
  } = useGetWfhsQuery(employeeId);

  const [createWfh] = useCreateWfhMutation();
  const [updateWfh] = useUpdateWfhMutation();

  useEffect(() => {
    if (attendancesData && !isAttendancesError) {
      setTotalPages(Math.ceil(attendancesData.length / ITEMS_PER_PAGE));
      const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
      const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
      setCurrentData(attendancesData.slice(indexOfFirstItem, indexOfLastItem));
      setFieldCounts(() => FieldCounter(attendancesData));
    } else {
      setCurrentData([]);
      setTotalPages(1);
    }
  }, [attendancesData, isAttendancesError, currentPage]);

  // Fetch server time for WFH display
  useEffect(() => {
    const fetchServerTime = async () => {
      const userTimezone = loggedUserInfo?.timezone; // uses system timezone if not set

      try {
        const res = await fetch("/api/current-time");
        if (res.ok) {
          const timeDetails = await res.json();
          if (timeDetails?.datetime) {
            const serverDate = new Date(timeDetails.datetime);

            const options = {
              weekday: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
              timeZone: userTimezone,
            };

            const formatter = new Intl.DateTimeFormat(undefined, options);
            const parts = formatter.formatToParts(serverDate);
            const weekDay = parts.find(x => x.type === "weekday")?.value || "";
            const dateNum = parts.find(x => x.type === "day")?.value || "";
            const hour = parts.find(x => x.type === "hour")?.value || "";
            const minute = parts.find(x => x.type === "minute")?.value || "";
            const dayPeriod = parts.find(x => x.type === "dayPeriod")?.value || "";

            const hrmin = `${hour}:${minute}`;
            const st = dayPeriod ? dayPeriod.toUpperCase() : "";

            const formatted = `${weekDay} ${dateNum}, ${hrmin} ${st}`;
            setDateTimeString(formatted);
          }
        }
      } catch (err) {}
    };

    fetchServerTime();

    const intervalId = setInterval(fetchServerTime, 30000);
    return () => { clearInterval(intervalId); };
  }, []);

  // Update WFH data when fetched
  useEffect(() => {
    if (wfhData) {
      dispatch(setWfhs(wfhData.wfhs));
      dispatch(setIsPunchedOut(wfhData.isPunchedOut));
      dispatch(setShowPunchWarning(wfhData.showPunchWarning));
    }
  }, [wfhData, dispatch]);

  const handleMonthChange = (e) => {
    setSelectedMonth(Number(e.target.value));
    setCurrentPage(1);
  };

  // Handle selfie capture completion
  const handleSelfieCapture = (file) => {
    setSelfieFile(file);
    // Immediately proceed with punch-in after capturing selfie
    performPunchIn(file);
  };

  // WFH Punch-In Handler
  const addPunchIn = async () => {
    // Open selfie capture modal instead of directly punching in
    setShowSelfieModal(true);
  };

  // Perform actual punch-in with selfie
  const performPunchIn = async (selfieFileToUpload) => {
    try {
      setProcessing(prev => ({ ...prev, punchIn: true }));

      // Create FormData to send selfie image
      const formData = new FormData();
      formData.append('selfie', selfieFileToUpload);

      const data = await createWfh(formData).unwrap();

      dispatch(addWfhToStore(data));
      dispatch(setIsPunchedOut(false));
      showSuccessToast("Punch-In successful");
      setSelfieFile(null);
    } catch (err) {
      if (err?.name === 'AbortError') {
        // Ignore AbortError
      }
    }

    setProcessing(prev => ({ ...prev, punchIn: false }));
  };

  // WFH Punch-Out Handler
  const updateWfhManage = async () => {
    try {
      setProcessing(prev => ({ ...prev, punchOut: true }));

      const data = await updateWfh().unwrap();

      dispatch(updateWfhInStore(data));
      dispatch(setIsPunchedOut(true));
      showSuccessToast("Punch-Out successful");
    } catch (err) {}

    setProcessing(prev => ({ ...prev, punchOut: false }));
  };

  return (
    <section className="section dashboard">
      <Row>
        <Col xs={12}>
          <ScreenRecordingBanner />
        </Col>
        <Col sm={7} className="order-2 order-sm-1">
          <Card className="info-card text-center">
            <Card.Body className="pb-0">
              <Row className="align-items-center">
                <Col lg={3}>
                  <h5 className="card-title">
                    <span className="position-relative">
                      Pending CL's
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-pending-cl">
                            Pending casual leave of last month
                          </Tooltip>
                        }
                      >
                        <i className="bi bi-info-circle"></i>
                      </OverlayTrigger>
                    </span>
                  </h5>
                  <div className="d-flex align-items-center justify-content-center mb-2 mt-3">
                    <h6>{loggedUserInfo?.cl ? loggedUserInfo.cl : 0}</h6>
                  </div>
                </Col>
                <Col lg={2}>
                  <h5 className="card-title">
                    <span className="position-relative">
                      Total Lates
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-lates">
                            Total lates of this month
                          </Tooltip>
                        }
                      >
                        <i className="bi bi-info-circle"></i>
                      </OverlayTrigger>
                    </span>
                  </h5>
                  <div className="d-flex align-items-center justify-content-center mb-2 mt-3">
                    <h6>{fieldCounts.late}</h6>
                  </div>
                </Col>
                <Col lg={3}>
                  <h5 className="card-title">
                    <span className="position-relative">
                      Total OT's
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-ot">
                            Total overtimes of this month
                          </Tooltip>
                        }
                      >
                        <i className="bi bi-info-circle"></i>
                      </OverlayTrigger>
                    </span>
                  </h5>
                  <div className="d-flex align-items-center justify-content-center mb-2 mt-3">
                    <h6>{fieldCounts.ot}</h6>
                  </div>
                </Col>
                <Col lg={4}>
                  <h5 className="card-title">
                    <span className="position-relative">
                      Total Ded. Days
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-dd">
                            Total deduction days of this month
                          </Tooltip>
                        }
                      >
                        <i className="bi bi-info-circle"></i>
                      </OverlayTrigger>
                    </span>
                  </h5>
                  <div className="d-flex align-items-center justify-content-center mb-2 mt-3">
                    <h6>
                      {loggedUserInfo?.totalDD ? loggedUserInfo.totalDD : 0}
                    </h6>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={5} className="order-3 order-sm-2">
          <PayrollNote
            year={attendanceYear}
            month={selectedMonth}
            employeeId={employeeId}
          />
        </Col>

        <Col md={7} className="order-4 order-md-2">
          <Card className="py-3">
            <Card.Body>
              <div className="d-flex justify-content-end mb-3">
                <Form.Group controlId="selectedMonth">
                  <Form.Select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                  >
                    <option value={currentMonthNumber}>
                      {currentMonthName}
                    </option>
                    <option value={previousMonthNumber}>
                      {previousMonthName}
                    </option>
                  </Form.Select>
                </Form.Group>
              </div>
              <Table striped bordered hover responsive className="text-center">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Punch In</th>
                    <th>Punch Out</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </td>
                        <td>{item.date ? item.date : "—"}</td>
                        <td>{item.day ? item.day : "—"}</td>
                        <td>{item.timeIn ? item.timeIn : "—"}</td>
                        <td>{item.timeOut ? item.timeOut : "—"}</td>
                        <td>{item.status ? item.status : "—"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr className="bg-light border text-center">
                      <td colSpan={6} className="text-muted">
                        {isAttendancesLoading
                          ? "Loading Data..."
                          : "No Data Found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {/* Use the HRPagination Component */}
              <div className="d-flex justify-content-end">
                <HRPagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {isAllowedToReadPayrollNotes && (
          <Col md={5} className="order-1 order-md-4">
            <Card>
              <Card.Body className="d-flex align-items-center justify-content-between gap-2 pt-4">
                <h5 className="mb-0">{dateTimeString || "STR DD, HH:MM ST"}</h5>
                <Button
                  variant="primary"
                  className="text-nowrap"
                  onClick={() => {
                    if (isPunchedOut) {
                      addPunchIn();
                    } else if (wfhs.length) {
                      updateWfhManage();
                    }
                  }}
                  disabled={
                    processing.punchIn ||
                    processing.punchOut ||
                    (!isPunchedOut && !wfhs.length)
                  }
                >
                  {processing.punchIn || processing.punchOut
                    ? 'Processing...'
                    : isPunchedOut
                    ? 'Punch-In'
                    : 'Punch-Out'}
                </Button>
              </Card.Body>
            </Card>

            <UpcomingHoliday />
          </Col>
        )}
      </Row>

      {/* Selfie Capture Modal */}
      <SelfieCapture
        show={showSelfieModal}
        onHide={() => setShowSelfieModal(false)}
        onCapture={handleSelfieCapture}
      />
    </section>
  );
};

export default GeneralAccessManagement;
